// Header component for Sunlight Grounds Care Client Portal
import { useState, useRef, useEffect } from 'react';
import { useProperty } from '@/hooks/useProperty';
import { Bell, ChevronDown, Check } from 'lucide-react';

export default function Header({ title, property, showGreeting, userName }) {
  const { properties, selectedProperty, selectProperty } = useProperty();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const activeProperty = property || selectedProperty;
  const hasMultiple = properties.length > 1;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getPropertyLabel = (prop) => {
    const city = prop?.address?.split(',')[1]?.trim() || '';
    return `${prop?.name} · ${city}`;
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const handleSwitch = (prop) => {
    selectProperty(prop);
    setShowDropdown(false);
  };

  return (
    <header className="bg-navy" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="app-header">
      {/* Logo row */}
      <div className="flex items-center justify-between px-5 pt-2 pb-2">
        <div className="flex items-center">
          <img
            src="/images/logo-white.png"
            alt="Sunlight Grounds Care"
            className="h-8 w-auto"
            data-testid="header-logo"
          />
        </div>
        <button
          className="p-2 -mr-2 text-white/70 hover:text-white transition-colors"
          aria-label="Notifications"
          data-testid="notifications-btn"
        >
          <Bell size={20} />
        </button>
      </div>

      {/* Title or Greeting */}
      <div className="px-5 pb-4">
        {showGreeting ? (
          <h1 className="font-cormorant text-[22px] font-semibold text-white" data-testid="header-greeting">
            {getGreeting()}, {userName}
          </h1>
        ) : (
          <h1 className="font-cormorant text-[22px] font-semibold text-white" data-testid="header-title">
            {title}
          </h1>
        )}

        {/* Property subtitle — clickable when multiple properties */}
        {activeProperty && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => hasMultiple && setShowDropdown((v) => !v)}
              className={`flex items-center gap-1 mt-1 ${
                hasMultiple ? 'cursor-pointer hover:text-white/70' : 'cursor-default'
              }`}
              data-testid="header-property-btn"
            >
              <p className="text-[11px] text-white/50 font-dm" data-testid="header-property">
                {getPropertyLabel(activeProperty)}
              </p>
              {hasMultiple && (
                <ChevronDown
                  size={12}
                  className={`text-white/50 transition-transform duration-200 ${
                    showDropdown ? 'rotate-180' : ''
                  }`}
                />
              )}
            </button>

            {/* Dropdown */}
            {showDropdown && (
              <div
                className="absolute left-0 top-full mt-2 w-72 bg-cream rounded-xl shadow-xl border border-black/[0.08] overflow-hidden z-[99999] animate-slide-up"
                data-testid="property-dropdown"
              >
                <p className="px-4 pt-3 pb-1.5 font-dm text-[9px] font-bold text-slate uppercase tracking-wider">
                  Switch property
                </p>
                {properties.map((prop) => {
                  const isActive = prop.id === activeProperty?.id;
                  return (
                    <button
                      key={prop.id}
                      onClick={() => handleSwitch(prop)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                        isActive ? 'bg-green-light' : 'hover:bg-slate-light'
                      }`}
                      data-testid={`property-option-${prop.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`font-dm text-[12px] font-bold truncate ${
                          isActive ? 'text-green' : 'text-navy'
                        }`}>
                          {prop.name}
                        </p>
                        <p className="font-dm text-[10px] text-slate truncate mt-0.5">
                          {prop.address}
                        </p>
                      </div>
                      {isActive && (
                        <Check size={16} className="text-green flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
